export const transformCasesToDashboardData = (cases, pnlSummary = null) => {
  const totalCases = cases.length;
  const activeCases = cases.filter(c => c.status !== "Funded" && c.status !== "Fall out");
  const totalActiveCases = activeCases.length;
  const approvedCases = cases.filter(c => c.status === 'Approved' || c.status === 'Cleared to Close');
  const closedCases = cases.filter(c => c.status === 'Cleared to Close');

  const loansClosed30d = closedCases.filter(c => {
    const closedDate = new Date(c.date);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return closedDate > thirtyDaysAgo;
  }).length;

  const approvalRate = totalActiveCases > 0 ? (approvedCases.length / totalActiveCases) * 100 : 0;
  const falloutRate = totalActiveCases > 0
    ? (cases.filter(c => c.status === "Fall out").length / totalActiveCases) * 100
    : 0;

  const pipelineStages = ["Processing", "Underwriting", "Action Required", "Approved", "Cleared to Close", "Closing", "Funded", "Fall out"];
  const pipeline = pipelineStages.map((stage) => {
    const count = cases.filter(c => c.status === stage).length;
    const percentOfActive = totalActiveCases > 0 ? (count / totalActiveCases) * 100 : 0;
    return { stage, count, percentOfActive };
  });

  const alerts = cases
    .filter(c => c.status === 'Action Required')
    .map(c => ({
      caseId: c.id || c.caseId || c.case_id,
      caseNumber: c.caseNumber,
      borrowerName: c.name,
      text: `Case requires attention.`,
      severity: 'med',
    }));

  const totalRevenue = Number(pnlSummary?.totalRevenue || 0);
  const totalExpenses = Math.abs(Number(pnlSummary?.totalExpenses || 0));
  const net = pnlSummary?.net !== undefined && pnlSummary?.net !== null
    ? Number(pnlSummary?.net || 0)
    : totalRevenue - totalExpenses;
  const caseCount = Number(pnlSummary?.caseCount || 0);
  const avgRevenuePerCase = pnlSummary?.avgRevenuePerCase !== undefined && pnlSummary?.avgRevenuePerCase !== null
    ? Number(pnlSummary?.avgRevenuePerCase || 0)
    : (caseCount > 0 ? totalRevenue / caseCount : 0);
  const avgNetPerCase = pnlSummary?.avgNetPerCase !== undefined && pnlSummary?.avgNetPerCase !== null
    ? Number(pnlSummary?.avgNetPerCase || 0)
    : (caseCount > 0 ? net / caseCount : 0);
  const pnlNetMargin = pnlSummary?.netMargin !== undefined && pnlSummary?.netMargin !== null
    ? Number(pnlSummary?.netMargin || 0) * 100
    : (totalRevenue > 0 ? (net / totalRevenue) * 100 : 0);

  return {
    periodLabel: "Last 30 Days",
    activePipelineCount: totalCases,
    activePipelineAmount: cases.reduce((acc, c) => acc + (parseFloat(String(c.loanAmount).replace(/[^0-9.]/g, '')) || 0), 0),
    kpis: {
      approvalRate,
      avgClearToCloseDays: 32.8, // Dummy data
      onTimeCloseRate: 88.2, // Dummy data
      avgConditionsPerFile: 4.1, // Dummy data
      falloutRate,
      loansClosed30d,
    },
    aiInsight: {
      severity: "info",
      text: "AI analysis is not yet available for this data.",
    },
    pipeline,
    cycleTimes: [
        { stage: "Application to Approval", medianDays: 12, p75Days: 18, slaTargetDays: 15, slaStatus: "good" },
        { stage: "Approval to CTC", medianDays: 15, p75Days: 22, slaTargetDays: 12, slaStatus: "warn" },
        { stage: "CTC to Funded", medianDays: 3, p75Days: 5, slaTargetDays: 2, slaStatus: "bad" },
        { stage: "Overall", medianDays: 30, p75Days: 45, slaTargetDays: 29, slaStatus: "bad" },
    ],
    qualityRisk: {
      metrics: [
        { label: "Files with > 5 Conditions", value: "18%" },
        { label: "Files with > 2 UW Touches", value: "25%" },
        { label: "Suspense/Declined Rate", value: "8.1%" },
        { label: "Post-Closing Issues", value: "3.5%" },
      ],
      riskSignals: [
        "High number of conditions on purchase loans in CA.",
        "Valuation disputes are up 15% month-over-month.",
        "Increased requests for income re-verification.",
      ],
    },
    borrowerExperience: {
        metrics: [
        { label: "Borrower NPS", value: "72" },
        { label: "Avg. Borrower-facing Tasks", value: "12" },
        { label: "Time in Borrower's Court", value: "4.2 days" },
        ],
        insight: "NPS scores are 15 points lower on loans with more than 3 processor touches. Empower processors to resolve issues faster.",
    },
    revenueProductivity: {
        metrics: [
        { label: "Total Revenue", value: totalRevenue },
        { label: "Total Expenses", value: totalExpenses },
        { label: "Net Income", value: net },
        { label: "Net Margin", value: pnlNetMargin },
        { label: "Revenue / Loan", value: avgRevenuePerCase },
        ],
    },
    pnlOverview: {
      totalRevenue,
      totalExpenses,
      net,
      netMargin: pnlNetMargin,
      avgRevenuePerCase,
      avgNetPerCase,
      caseCount
    },
    alerts,
    performanceScore: {
        speed: 82,
        quality: 75,
        borrowerUx: 88,
        conversion: 91,
        overall: 84,
        rankLabel: "Top 15%",
    },
    trends: [
        { label: "Avg. Cycle Time", from: "34.1d", to: "32.8d", direction: "down" },
        { label: "Fallout Rate", from: "8.5%", to: "7.9%", direction: "down" },
        { label: "Funded Volume", from: "$22.1M", to: "$24.8M", direction: "up" },
    ],
  };
};
